import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { reportAPI, ticketAPI, userAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';

const Reports = () => {
  const [activeTab, setActiveTab] = useState('daily');
  const [daily, setDaily] = useState(null);
  const [pending, setPending] = useState([]);
  const [performance, setPerformance] = useState([]);
  const [subDists, setSubDists] = useState([]);
  const [selectedSubDist, setSelectedSubDist] = useState('');
  const [selectedTech, setSelectedTech] = useState('');
  const [escalation, setEscalation] = useState(null);
  const [sla, setSla] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [showDownload, setShowDownload] = useState(false);
  const [dateRange, setDateRange] = useState({
    type: 'today',
    start_date: new Date().toISOString().split('T')[0],
    end_date: new Date().toISOString().split('T')[0],
    report_type: 'summary'
  });
  const { tenant, user } = useAuth();
  const navigate = useNavigate();
  const primaryColor = tenant ? tenant.branding.primary_color || '#1B6B3A' : '#1B6B3A';

  const canDownload = user && (user.role === 'superadmin' || user.role === 'isp_admin' || user.role === 'dist_head' || user.role === 'director');
  const [showPreview, setShowPreview] = useState(false);
  const [previewData, setPreviewData] = useState({ tickets:[], loading:false });
  const [previewFilter, setPreviewFilter] = useState({ type:'today', start:'', end:'', tech:'', subdist:'' });
  const [previewTechs, setPreviewTechs] = useState([]);

  const loadPreview = async (f) => {
    const filter = f || previewFilter;
    setPreviewData(p => ({...p, loading:true}));
    try {
      const today = new Date();
      const fmt = d => d.toISOString().split('T')[0];
      let start, end;
      if(filter.type==='today') { start=fmt(today); end=fmt(today); }
      else if(filter.type==='yesterday') { const y=new Date(today); y.setDate(y.getDate()-1); start=end=fmt(y); }
      else if(filter.type==='week') { const w=new Date(today); w.setDate(w.getDate()-w.getDay()); start=fmt(w); end=fmt(today); }
      else if(filter.type==='month') { start=fmt(new Date(today.getFullYear(),today.getMonth(),1)); end=fmt(today); }
      else if(filter.type==='last_month') { start=fmt(new Date(today.getFullYear(),today.getMonth()-1,1)); end=fmt(new Date(today.getFullYear(),today.getMonth(),0)); }
      else { start=filter.start; end=filter.end; }
      const res = await ticketAPI.getAll({ limit:1000 });
      let tickets = res.data.tickets || [];
      if(start && end) {
        const s=new Date(start+'T00:00:00'), e=new Date(end+'T23:59:59');
        tickets = tickets.filter(t => { const c=new Date(t.createdAt); return c>=s && c<=e; });
      }
      if(start && end) {
        const s=new Date(start+'T00:00:00'), e=new Date(end+'T23:59:59');
        tickets = tickets.filter(t => { const c=new Date(t.createdAt); return c>=s && c<=e; });
      }
      // Filter by subdist first (get all techs in subdist)
      if(filter.subdist) {
        const techIds = previewTechs.filter(u=>String(u.subdist_id)===String(filter.subdist)).map(u=>String(u._id));
        tickets = tickets.filter(t => {
          if(!t.assigned_to) return false;
          const aid = typeof t.assigned_to==='object' ? String(t.assigned_to._id) : String(t.assigned_to);
          return techIds.includes(aid);
        });
      }
      // Then filter by specific tech if selected
      if(filter.tech) {
        tickets = tickets.filter(t => {
          if(!t.assigned_to) return false;
          if(typeof t.assigned_to === 'object') return String(t.assigned_to._id)===filter.tech;
          return String(t.assigned_to)===filter.tech;
        });
      }
      setPreviewData({ tickets, loading:false });
    } catch(e) { setPreviewData({ tickets:[], loading:false }); }
  };

  const openPreview = async () => {
    setShowPreview(true);
    if(previewTechs.length===0) {
      try {
        const techRes = await userAPI.getTechnicians();
        const techs = techRes.data.technicians || techRes.data.users || [];
        // Enrich with subdist_name from existing subDists state
        const enriched = techs.map(t => ({
          ...t,
          subdist_name: (subDists.find(s => String(s._id)===String(t.subdist_id)) || {}).name || ''
        }));
        setPreviewTechs(enriched);
      } catch(e){ console.error(e); }
    }
    loadPreview();
  };

  useEffect(() => {
    Promise.all([
      reportAPI.dailySummary(),
      reportAPI.pendingTickets(),
      reportAPI.technicianPerformance(),
      reportAPI.escalationReport(),
      reportAPI.slaBreachReport(),
      fetch('/api/network/subdistributions', {headers:{Authorization:'Bearer '+localStorage.getItem('token')}}).then(r=>r.json())
    ]).then(([d, p, perf, esc, s, sd]) => {
      setDaily(d.data);
      setPending(p.data.tickets);
      setPerformance(perf.data.performance || []);
      setEscalation(esc.data);
      setSla(s.data);
      setSubDists(sd.subdistributions || []);
    }).finally(() => setLoading(false));
  }, []);

  const getDateRange = (type) => {
    const today = new Date();
    const fmt = (d) => d.toISOString().split('T')[0];
    switch(type) {
      case 'today':
        return { start: fmt(today), end: fmt(today) };
      case 'yesterday':
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        return { start: fmt(yesterday), end: fmt(yesterday) };
      case 'this_week':
        const weekStart = new Date(today);
        weekStart.setDate(today.getDate() - today.getDay());
        return { start: fmt(weekStart), end: fmt(today) };
      case 'last_week':
        const lastWeekEnd = new Date(today);
        lastWeekEnd.setDate(today.getDate() - today.getDay() - 1);
        const lastWeekStart = new Date(lastWeekEnd);
        lastWeekStart.setDate(lastWeekEnd.getDate() - 6);
        return { start: fmt(lastWeekStart), end: fmt(lastWeekEnd) };
      case 'this_month':
        const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
        return { start: fmt(monthStart), end: fmt(today) };
      case 'last_month':
        const lastMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0);
        const lastMonthStart = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        return { start: fmt(lastMonthStart), end: fmt(lastMonthEnd) };
      default:
        return { start: dateRange.start_date, end: dateRange.end_date };
    }
  };

  const handleDateTypeChange = (type) => {
    const range = getDateRange(type);
    setDateRange({...dateRange, type, start_date: range.start, end_date: range.end });
  };

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const range = dateRange.type === 'custom'
        ? { start: dateRange.start_date, end: dateRange.end_date }
        : getDateRange(dateRange.type);

      const res = await ticketAPI.getAll({ limit: 1000 });
      const tickets = res.data.tickets || [];

      const startDate = new Date(range.start + 'T00:00:00');
      const endDate = new Date(range.end + 'T23:59:59');

      const filtered = tickets.filter(t => {
        const created = new Date(t.createdAt);
        return created >= startDate && created <= endDate;
      });

      let csvRows = [];
      const tenantName = tenant ? tenant.branding.app_name : 'FibraDesk';

      if (dateRange.report_type === 'summary') {
        const opened = filtered.length;
        const resolved = filtered.filter(t => t.status === 'resolved' || t.status === 'closed').length;
        const pending = filtered.filter(t => !['resolved', 'closed'].includes(t.status)).length;
        const escalated = filtered.filter(t => t.status === 'escalated').length;
        const slaBreached = filtered.filter(t => t.sla && t.sla.breached).length;
        csvRows = [
          [tenantName + ' — Summary Report'],
          ['Period: ' + range.start + ' to ' + range.end],
          ['Generated: ' + new Date().toLocaleString()],
          [''],
          ['SUMMARY'],
          ['Total Tickets', opened],
          ['Resolved/Closed', resolved],
          ['Pending', pending],
          ['Escalated', escalated],
          ['SLA Breached', slaBreached],
          ['Resolution Rate', opened > 0 ? Math.round((resolved/opened)*100) + '%' : '0%'],
          [''],
          ['BY CATEGORY'],
          ['Category', 'Count'],
        ];
        const byCategory = {};
        filtered.forEach(t => {
          byCategory[t.complaint.category] = (byCategory[t.complaint.category] || 0) + 1;
        });
        Object.entries(byCategory).forEach(([cat, count]) => csvRows.push([cat, count]));
        csvRows.push(['']);
        csvRows.push(['BY PRIORITY']);
        csvRows.push(['Priority', 'Count']);
        const byPriority = {};
        filtered.forEach(t => {
          byPriority[t.complaint.priority] = (byPriority[t.complaint.priority] || 0) + 1;
        });
        Object.entries(byPriority).forEach(([p, count]) => csvRows.push([p, count]));

      } else if (dateRange.report_type === 'tickets') {
        csvRows = [
          [tenantName + ' — Ticket List Report'],
          ['Period: ' + range.start + ' to ' + range.end],
          ['Generated: ' + new Date().toLocaleString()],
          [''],
          ['Ticket No', 'Customer', 'Phone', 'Category', 'Priority', 'Status', 'Assigned To', 'Created', 'SLA Deadline']
        ];
        filtered.forEach(t => {
          csvRows.push([
            t.ticket_number,
            t.customer.name,
            t.customer.phone,
            t.complaint.category,
            t.complaint.priority,
            t.status,
            t.assigned_to ? t.assigned_to.name : 'Unassigned',
            new Date(t.createdAt).toLocaleString(),
            t.sla ? new Date(t.sla.deadline).toLocaleString() : '-'
          ]);
        });

      } else if (dateRange.report_type === 'escalation') {
        const escalatedTickets = filtered.filter(t => t.escalation && t.escalation.escalation_count > 0);
        csvRows = [
          [tenantName + ' — Escalation Report'],
          ['Period: ' + range.start + ' to ' + range.end],
          ['Generated: ' + new Date().toLocaleString()],
          [''],
          ['Total Escalated', escalatedTickets.length],
          [''],
          ['Ticket No', 'Customer', 'Category', 'Escalation Level', 'Status', 'Created']
        ];
        escalatedTickets.forEach(t => {
          csvRows.push([
            t.ticket_number,
            t.customer.name,
            t.complaint.category,
            t.escalation.current_level,
            t.status,
            new Date(t.createdAt).toLocaleString()
          ]);
        });

      } else if (dateRange.report_type === 'sla') {
        const slaTickets = filtered.filter(t => t.sla && t.sla.breached);
        csvRows = [
          [tenantName + ' — SLA Breach Report'],
          ['Period: ' + range.start + ' to ' + range.end],
          ['Generated: ' + new Date().toLocaleString()],
          [''],
          ['Total SLA Breached', slaTickets.length],
          [''],
          ['Ticket No', 'Customer', 'Category', 'Priority', 'Assigned To', 'SLA Deadline', 'Status']
        ];
        slaTickets.forEach(t => {
          csvRows.push([
            t.ticket_number,
            t.customer.name,
            t.complaint.category,
            t.complaint.priority,
            t.assigned_to ? t.assigned_to.name : 'Unassigned',
            t.sla ? new Date(t.sla.deadline).toLocaleString() : '-',
            t.status
          ]);
        });
      }

      const csvContent = csvRows.map(r => r.map(cell => '"' + String(cell).replace(/"/g, '""') + '"').join(',')).join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = tenantName + '_' + dateRange.report_type + '_' + range.start + '_to_' + range.end + '.csv';
      a.click();
      URL.revokeObjectURL(url);
      setShowDownload(false);
    } catch (err) {
      alert('Failed to download report: ' + err.message);
    } finally { setDownloading(false); }
  };

  const tabStyle = (tab) => ({ padding: '10px 20px', border: 'none', borderBottom: activeTab === tab ? '3px solid ' + primaryColor : '3px solid transparent', background: 'transparent', color: activeTab === tab ? primaryColor : '#666', fontWeight: activeTab === tab ? '700' : '400', cursor: 'pointer', fontSize: '14px' });
  const cardStyle = { background: 'white', borderRadius: '10px', padding: '20px', boxShadow: '0 2px 10px rgba(0,0,0,0.08)', marginBottom: '20px' };
  const priorityColor = { high: '#f44336', critical: '#b71c1c', medium: '#ff9800', low: '#4caf50' };
  const statusColor = { open: '#2196f3', assigned: '#9c27b0', in_progress: '#ff9800', escalated: '#f44336', resolved: '#4caf50', closed: '#757575' };
  const inputStyle = { padding: '8px 12px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '14px' };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h2 style={{ margin: '0', color: '#333' }}>Reports</h2>
        {canDownload && (
          <>
          <button onClick={openPreview} style={{ background: '#1976d2', color: 'white', border: 'none', padding: '10px 20px', borderRadius: '6px', cursor: 'pointer', fontWeight: '600', marginRight:'8px' }}>
            👁️ Preview
          </button>
          <button onClick={() => setShowDownload(!showDownload)} style={{ background: primaryColor, color: 'white', border: 'none', padding: '10px 20px', borderRadius: '6px', cursor: 'pointer', fontWeight: '600' }}>
            ⬇️ {showDownload ? 'Cancel Download' : 'Download Report'}
          </button>
          </>
        )}
      </div>

      {showPreview && (
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.6)',zIndex:1000,display:'flex',alignItems:'center',justifyContent:'center',padding:'16px'}}>
          <div style={{background:'white',borderRadius:'12px',padding:'20px',width:'100%',maxWidth:'960px',maxHeight:'90vh',overflowY:'auto'}}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'16px'}}>
              <h3 style={{margin:0,color:'#333'}}>📊 Report Preview</h3>
              <button onClick={()=>setShowPreview(false)} style={{padding:'6px 14px',background:'#f44336',color:'white',border:'none',borderRadius:'6px',cursor:'pointer',fontWeight:'600'}}>✕ Close</button>
            </div>
            <div style={{display:'flex',flexWrap:'wrap',gap:'10px',marginBottom:'16px',padding:'12px',background:'#f5f5f5',borderRadius:'8px',alignItems:'flex-end'}}>
              <div>
                <label style={{fontSize:'12px',fontWeight:'600',color:'#555',display:'block',marginBottom:'4px'}}>Period</label>
                <select value={previewFilter.type} onChange={e=>{const f={...previewFilter,type:e.target.value};setPreviewFilter(f);loadPreview(f);}} style={{padding:'8px 12px',border:'1px solid #ddd',borderRadius:'6px',fontSize:'13px'}}>
                  <option value="today">Today</option>
                  <option value="yesterday">Yesterday</option>
                  <option value="week">This Week</option>
                  <option value="month">This Month</option>
                  <option value="last_month">Last Month</option>
                  <option value="custom">Custom</option>
                </select>
              </div>
              {previewFilter.type==='custom' && <>
                <div>
                  <label style={{fontSize:'12px',fontWeight:'600',color:'#555',display:'block',marginBottom:'4px'}}>From</label>
                  <input type="date" value={previewFilter.start} onChange={e=>setPreviewFilter(f=>({...f,start:e.target.value}))} style={{padding:'8px 12px',border:'1px solid #ddd',borderRadius:'6px',fontSize:'13px'}} />
                </div>
                <div>
                  <label style={{fontSize:'12px',fontWeight:'600',color:'#555',display:'block',marginBottom:'4px'}}>To</label>
                  <input type="date" value={previewFilter.end} onChange={e=>setPreviewFilter(f=>({...f,end:e.target.value}))} style={{padding:'8px 12px',border:'1px solid #ddd',borderRadius:'6px',fontSize:'13px'}} />
                </div>
              </>}
              <div>
                <label style={{fontSize:'12px',fontWeight:'600',color:'#555',display:'block',marginBottom:'4px'}}>Sub-Dist</label>
                <select value={previewFilter.subdist} onChange={e=>{const f={...previewFilter,subdist:e.target.value,tech:''};setPreviewFilter(f);loadPreview(f);}} style={{padding:'8px 12px',border:'1px solid #ddd',borderRadius:'6px',fontSize:'13px'}}>
                  <option value="">All Sub-Dist</option>
                  {subDists.map(s=>(
                    <option key={s._id} value={String(s._id)}>{s.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label style={{fontSize:'12px',fontWeight:'600',color:'#555',display:'block',marginBottom:'4px'}}>Technician</label>
                <select value={previewFilter.tech} onChange={e=>{const f={...previewFilter,tech:e.target.value};setPreviewFilter(f);loadPreview(f);}} style={{padding:'8px 12px',border:'1px solid #ddd',borderRadius:'6px',fontSize:'13px'}}>
                  <option value="">All Techs</option>
                  {(previewFilter.subdist ? previewTechs.filter(u=>String(u.subdist_id)===String(previewFilter.subdist)) : previewTechs).map(u=>(
                    <option key={u._id} value={String(u._id)}>{u.name}</option>
                  ))}
                </select>
              </div>
              {previewFilter.type==='custom' && (
                <button onClick={()=>loadPreview()} style={{padding:'8px 16px',background:'#1976d2',color:'white',border:'none',borderRadius:'6px',cursor:'pointer',fontWeight:'600'}}>🔄 Apply</button>
              )}
            </div>
            {previewData.loading ? (
              <div style={{textAlign:'center',padding:'40px',color:'#666'}}>Loading...</div>
            ) : (
              <>
                <div style={{marginBottom:'12px',fontSize:'13px',color:'#666'}}>
                  Showing <strong>{previewData.tickets.length}</strong> tickets
                  {' | '}Resolved: <strong style={{color:'#4caf50'}}>{previewData.tickets.filter(t=>t.status==='resolved'||t.status==='closed').length}</strong>
                  {' | '}Pending: <strong style={{color:'#ff9800'}}>{previewData.tickets.filter(t=>!['resolved','closed'].includes(t.status)).length}</strong>
                </div>
                <div style={{overflowX:'auto'}}>
                  <table style={{width:'100%',borderCollapse:'collapse',fontSize:'13px'}}>
                    <thead>
                      <tr style={{background:'#f5f5f5'}}>
                        <th style={{padding:'10px',textAlign:'left',borderBottom:'2px solid #ddd'}}>Ticket #</th>
                        <th style={{padding:'10px',textAlign:'left',borderBottom:'2px solid #ddd'}}>Customer</th>
                        <th style={{padding:'10px',textAlign:'left',borderBottom:'2px solid #ddd'}}>Complaint</th>
                        <th style={{padding:'10px',textAlign:'center',borderBottom:'2px solid #ddd'}}>Status</th>
                        <th style={{padding:'10px',textAlign:'left',borderBottom:'2px solid #ddd'}}>Technician</th>
                        <th style={{padding:'10px',textAlign:'left',borderBottom:'2px solid #ddd'}}>Created</th>
                      </tr>
                    </thead>
                    <tbody>
                      {previewData.tickets.slice(0,100).map((t,i)=>(
                        <tr key={i} style={{borderBottom:'1px solid #eee',background:i%2===0?'white':'#fafafa'}}>
                          <td style={{padding:'10px',fontFamily:'monospace',fontSize:'11px'}}>{t.ticket_number||String(t._id||'').slice(-6)}</td>
                          <td style={{padding:'10px'}}>{t.customer?.name||'-'}</td>
                          <td style={{padding:'10px',fontSize:'12px'}}>{t.complaint?.category||t.complaint?.description||'-'}</td>
                          <td style={{padding:'10px',textAlign:'center'}}>
                            <span style={{padding:'3px 8px',borderRadius:'12px',fontSize:'11px',fontWeight:'600',background:t.status==='resolved'?'#e8f5e9':t.status==='pending'?'#fff3e0':'#e3f2fd',color:t.status==='resolved'?'#2e7d32':t.status==='pending'?'#e65100':'#1565c0'}}>{t.status}</span>
                          </td>
                          <td style={{padding:'10px',fontSize:'12px'}}>{t.assigned_to&&typeof t.assigned_to==='object'?t.assigned_to.name:'-'}</td>
                          <td style={{padding:'10px',fontSize:'11px',color:'#666'}}>{t.createdAt?new Date(t.createdAt).toLocaleDateString():'-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {previewData.tickets.length>100 && <p style={{textAlign:'center',color:'#666',marginTop:'12px',fontSize:'13px'}}>Showing 100 of {previewData.tickets.length} — Download for full report</p>}
                </div>
              </>
            )}
          </div>
        </div>
      )}
      {showDownload && canDownload && (
        <div style={{ background: 'white', borderRadius: '10px', padding: '24px', boxShadow: '0 2px 10px rgba(0,0,0,0.08)', marginBottom: '20px', border: '2px solid ' + primaryColor }}>
          <h3 style={{ margin: '0 0 20px', color: primaryColor }}>⬇️ Download Report</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#333' }}>Report Type</label>
              <select value={dateRange.report_type} onChange={e => setDateRange({...dateRange, report_type: e.target.value})} style={{...inputStyle, width: '100%'}}>
                <option value="summary">Summary Report</option>
                <option value="tickets">Full Ticket List</option>
                <option value="escalation">Escalation Report</option>
                <option value="sla">SLA Breach Report</option>
              </select>
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#333' }}>Date Range</label>
              <select value={dateRange.type} onChange={e => handleDateTypeChange(e.target.value)} style={{...inputStyle, width: '100%'}}>
                <option value="today">Today</option>
                <option value="yesterday">Yesterday</option>
                <option value="this_week">This Week</option>
                <option value="last_week">Last Week</option>
                <option value="this_month">This Month</option>
                <option value="last_month">Last Month</option>
                <option value="custom">Custom Range</option>
              </select>
            </div>
          </div>
          {dateRange.type === 'custom' && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#333' }}>Start Date</label>
                <input type="date" value={dateRange.start_date} onChange={e => setDateRange({...dateRange, start_date: e.target.value})} style={{...inputStyle, width: '100%', boxSizing: 'border-box'}} />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#333' }}>End Date</label>
                <input type="date" value={dateRange.end_date} onChange={e => setDateRange({...dateRange, end_date: e.target.value})} style={{...inputStyle, width: '100%', boxSizing: 'border-box'}} />
              </div>
            </div>
          )}
          <div style={{ background: '#e8f5e9', padding: '12px 16px', borderRadius: '6px', marginBottom: '16px' }}>
            <p style={{ margin: '0', fontSize: '13px', color: '#2e7d32' }}>
              📅 Period: <strong>{dateRange.start_date}</strong> to <strong>{dateRange.end_date}</strong> &nbsp;|&nbsp;
              📊 Report: <strong>{dateRange.report_type === 'summary' ? 'Summary' : dateRange.report_type === 'tickets' ? 'Full Ticket List' : dateRange.report_type === 'escalation' ? 'Escalation' : 'SLA Breach'}</strong>
            </p>
          </div>
          <button onClick={handleDownload} disabled={downloading} style={{ padding: '12px 32px', background: downloading ? '#ccc' : primaryColor, color: 'white', border: 'none', borderRadius: '6px', cursor: downloading ? 'not-allowed' : 'pointer', fontWeight: '600', fontSize: '15px' }}>
            {downloading ? 'Preparing Download...' : '⬇️ Download CSV'}
          </button>
          <p style={{ margin: '8px 0 0', fontSize: '12px', color: '#666' }}>File will be downloaded as CSV — open with Excel or Google Sheets</p>
        </div>
      )}

      <div style={{ background: 'white', borderRadius: '10px', boxShadow: '0 2px 10px rgba(0,0,0,0.08)', marginBottom: '20px' }}>
        <div style={{ display: 'flex', borderBottom: '1px solid #f0f0f0', padding: '0 16px' }}>
          <button style={tabStyle('daily')} onClick={() => setActiveTab('daily')}>Daily Summary</button>
          <button style={tabStyle('pending')} onClick={() => setActiveTab('pending')}>Pending Tickets</button>
          <button style={tabStyle('performance')} onClick={() => setActiveTab('performance')}>Tech Performance</button>
          <button style={tabStyle('escalation')} onClick={() => setActiveTab('escalation')}>Escalations</button>
          <button style={tabStyle('sla')} onClick={() => setActiveTab('sla')}>SLA Breach</button>
        </div>
      </div>
      {loading ? <p style={{ textAlign: 'center' }}>Loading reports...</p> : (
        <div>
          {activeTab === 'daily' && daily && (
            <div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '16px', marginBottom: '20px' }}>
                {[
                  { label: 'Opened Today', value: daily.summary.opened, color: '#2196f3' },
                  { label: 'Closed Today', value: daily.summary.closed, color: '#4caf50' },
                  { label: 'Pending', value: daily.summary.pending, color: '#ff9800' },
                  { label: 'Escalated', value: daily.summary.escalated, color: '#f44336' },
                  { label: 'SLA Breached', value: daily.summary.sla_breached, color: '#b71c1c' }
                ].map(function(item) {
                  return (
                    <div key={item.label} style={{ background: 'white', padding: '16px', borderRadius: '10px', boxShadow: '0 2px 10px rgba(0,0,0,0.08)', borderLeft: '4px solid ' + item.color, textAlign: 'center' }}>
                      <h3 style={{ margin: '0 0 8px', color: item.color, fontSize: '28px' }}>{item.value}</h3>
                      <p style={{ margin: '0', color: '#666', fontSize: '12px' }}>{item.label}</p>
                    </div>
                  );
                })}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                <div style={cardStyle}>
                  <h3 style={{ margin: '0 0 16px', color: '#333' }}>By Category</h3>
                  {daily.by_category.length === 0 ? <p style={{ color: '#666' }}>No data yet</p> : daily.by_category.map(function(c) {
                    return (
                      <div key={c._id} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #f0f0f0' }}>
                        <span style={{ fontSize: '13px' }}>{c._id}</span>
                        <span style={{ fontWeight: '700', color: primaryColor }}>{c.count}</span>
                      </div>
                    );
                  })}
                </div>
                <div style={cardStyle}>
                  <h3 style={{ margin: '0 0 16px', color: '#333' }}>By Priority</h3>
                  {daily.by_priority.length === 0 ? <p style={{ color: '#666' }}>No data yet</p> : daily.by_priority.map(function(p) {
                    return (
                      <div key={p._id} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #f0f0f0' }}>
                        <span style={{ background: priorityColor[p._id] + '20', color: priorityColor[p._id], padding: '3px 8px', borderRadius: '4px', fontSize: '12px', fontWeight: '600' }}>{p._id}</span>
                        <span style={{ fontWeight: '700' }}>{p.count}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
          {activeTab === 'pending' && (
            <div style={cardStyle}>
              <h3 style={{ margin: '0 0 16px', color: '#333' }}>Pending Tickets ({pending.length})</h3>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#f5f5f5' }}>
                    <th style={{ padding: '10px', textAlign: 'left', fontSize: '13px' }}>Ticket</th>
                    <th style={{ padding: '10px', textAlign: 'left', fontSize: '13px' }}>Customer</th>
                    <th style={{ padding: '10px', textAlign: 'left', fontSize: '13px' }}>Category</th>
                    <th style={{ padding: '10px', textAlign: 'left', fontSize: '13px' }}>Status</th>
                    <th style={{ padding: '10px', textAlign: 'left', fontSize: '13px' }}>Age</th>
                    <th style={{ padding: '10px', textAlign: 'left', fontSize: '13px' }}>SLA Left</th>
                    <th style={{ padding: '10px', textAlign: 'left', fontSize: '13px' }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {pending.length === 0 ? <tr><td colSpan="7" style={{ padding: '20px', textAlign: 'center', color: '#666' }}>No pending tickets!</td></tr> : pending.map(function(t) {
                    return (
                      <tr key={t._id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                        <td style={{ padding: '10px', fontSize: '13px', fontWeight: '600', color: primaryColor }}>{t.ticket_number}</td>
                        <td style={{ padding: '10px', fontSize: '13px' }}>{t.customer.name}</td>
                        <td style={{ padding: '10px', fontSize: '13px' }}>{t.complaint.category}</td>
                        <td style={{ padding: '10px' }}><span style={{ background: statusColor[t.status] + '20', color: statusColor[t.status], padding: '3px 8px', borderRadius: '4px', fontSize: '12px' }}>{t.status}</span></td>
                        <td style={{ padding: '10px', fontSize: '13px' }}>{t.age_hours}h</td>
                        <td style={{ padding: '10px', fontSize: '13px', color: t.sla_remaining_hours < 4 ? '#f44336' : '#333', fontWeight: t.sla_remaining_hours < 4 ? '700' : '400' }}>{t.sla_remaining_hours}h</td>
                        <td style={{ padding: '10px' }}><button onClick={() => navigate('/tickets/' + t._id)} style={{ background: primaryColor, color: 'white', border: 'none', padding: '5px 12px', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}>View</button></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          {activeTab === 'performance' && (
            <div>
              {/* Filters */}
              <div style={{ display:'flex', gap:'12px', marginBottom:'20px', flexWrap:'wrap', alignItems:'center' }}>
                <select value={selectedSubDist} onChange={e=>{setSelectedSubDist(e.target.value);setSelectedTech('');}}
                  style={{ padding:'10px 14px', border:'1px solid #e2e8f0', borderRadius:'8px', fontSize:'14px', minWidth:'200px', background:'white' }}>
                  <option value="">All Sub-Distributions</option>
                  {subDists.map(sd=><option key={sd._id} value={sd._id}>{sd.name}</option>)}
                </select>
                <select value={selectedTech} onChange={e=>setSelectedTech(e.target.value)}
                  style={{ padding:'10px 14px', border:'1px solid #e2e8f0', borderRadius:'8px', fontSize:'14px', minWidth:'200px', background:'white' }}>
                  <option value="">All Technicians</option>
                  {performance
                    .filter(p => !selectedSubDist || p.sub_dist_id === selectedSubDist)
                    .map(p=><option key={p._id} value={p._id}>{p.name}</option>)}
                </select>
                {(selectedSubDist||selectedTech) && <button onClick={()=>{setSelectedSubDist('');setSelectedTech('');}}
                  style={{ padding:'10px 16px', background:'#f1f5f9', border:'1px solid #e2e8f0', borderRadius:'8px', cursor:'pointer', fontSize:'13px', fontWeight:'600' }}>Clear</button>}
                <div style={{ marginLeft:'auto', fontSize:'13px', color:'#64748b' }}>
                  {performance.filter(p=>(!selectedSubDist||p.sub_dist_id===selectedSubDist)&&(!selectedTech||p._id===selectedTech)).length} technicians
                </div>
              </div>

              {/* Performance Cards */}
              <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(220px,1fr))', gap:'14px', marginBottom:'24px' }}>
                {performance
                  .filter(p=>(!selectedSubDist||p.sub_dist_id===selectedSubDist)&&(!selectedTech||p._id===selectedTech))
                  .map((p,i) => {
                    const rate = p.resolution_rate || 0;
                    const color = rate>=80?'#10b981':rate>=50?'#f59e0b':'#ef4444';
                    const bg = rate>=80?'#dcfce7':rate>=50?'#fef3c7':'#fee2e2';
                    return (
                      <div key={i} style={{ background:'white', border:'1px solid #e2e8f0', borderRadius:'12px', padding:'16px', boxShadow:'0 1px 3px rgba(0,0,0,0.04)' }}>
                        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:'12px' }}>
                          <div>
                            <div style={{ fontSize:'14px', fontWeight:'700', color:'#0f172a', marginBottom:'2px' }}>{p.name}</div>
                            <div style={{ fontSize:'11px', color:'#94a3b8' }}>{(p.role||'').replace(/_/g,' ')}</div>
                            {p.sub_dist_name && <div style={{ fontSize:'11px', color:'#6366f1', marginTop:'2px' }}>{p.sub_dist_name}</div>}
                          </div>
                          <span style={{ background:bg, color:color, padding:'4px 10px', borderRadius:'20px', fontSize:'12px', fontWeight:'700' }}>
                            {rate>=80?'Excellent':rate>=50?'Good':'Needs Attention'}
                          </span>
                        </div>
                        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr 1fr', gap:'8px', textAlign:'center' }}>
                          <div style={{ background:'#f8fafc', borderRadius:'8px', padding:'8px 4px' }}>
                            <div style={{ fontSize:'18px', fontWeight:'800', color:'#6366f1' }}>{p.total_assigned||0}</div>
                            <div style={{ fontSize:'10px', color:'#94a3b8', marginTop:'2px' }}>Assigned</div>
                          </div>
                          <div style={{ background:'#f0fdf4', borderRadius:'8px', padding:'8px 4px' }}>
                            <div style={{ fontSize:'18px', fontWeight:'800', color:'#10b981' }}>{p.resolved||0}</div>
                            <div style={{ fontSize:'10px', color:'#94a3b8', marginTop:'2px' }}>Resolved</div>
                          </div>
                          <div style={{ background:'#fff7ed', borderRadius:'8px', padding:'8px 4px' }}>
                            <div style={{ fontSize:'18px', fontWeight:'800', color:'#f59e0b' }}>{p.pending||0}</div>
                            <div style={{ fontSize:'10px', color:'#94a3b8', marginTop:'2px' }}>Pending</div>
                          </div>
                          <div style={{ background:bg, borderRadius:'8px', padding:'8px 4px' }}>
                            <div style={{ fontSize:'18px', fontWeight:'800', color:color }}>{rate}%</div>
                            <div style={{ fontSize:'10px', color:'#94a3b8', marginTop:'2px' }}>Rate</div>
                          </div>
                        </div>
                        {p.avg_resolution_hours !== undefined && (
                          <div style={{ marginTop:'10px', padding:'6px 10px', background:'#f8fafc', borderRadius:'8px', fontSize:'12px', color:'#64748b', display:'flex', justifyContent:'space-between' }}>
                            <span>Avg resolution</span>
                            <span style={{ fontWeight:'600', color:'#0f172a' }}>{p.avg_resolution_hours}h</span>
                          </div>
                        )}
                      </div>
                    );
                  })
                }
                {performance.filter(p=>(!selectedSubDist||p.sub_dist_id===selectedSubDist)&&(!selectedTech||p._id===selectedTech)).length === 0 && (
                  <div style={{ gridColumn:'1/-1', textAlign:'center', padding:'40px', color:'#94a3b8', fontSize:'14px' }}>
                    No technician data found for selected filter
                  </div>
                )}
              </div>
            </div>
          )}
                    {activeTab === 'escalation' && escalation && (
            <div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                <div style={cardStyle}>
                  <h3 style={{ margin: '0 0 4px', color: '#333' }}>Total Escalated</h3>
                  <h1 style={{ margin: '0 0 16px', color: '#f44336', fontSize: '48px' }}>{escalation.total_escalated}</h1>
                  <h4 style={{ margin: '0 0 12px', color: '#333' }}>By Level</h4>
                  {escalation.by_level.length === 0 ? <p style={{ color: '#666' }}>No escalations</p> : escalation.by_level.map(function(l) {
                    return (
                      <div key={l._id} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #f0f0f0' }}>
                        <span style={{ fontSize: '13px' }}>Level {l._id}</span>
                        <span style={{ fontWeight: '700', color: '#f44336' }}>{l.count}</span>
                      </div>
                    );
                  })}
                </div>
                <div style={cardStyle}>
                  <h3 style={{ margin: '0 0 16px', color: '#333' }}>By Category</h3>
                  {escalation.by_category.length === 0 ? <p style={{ color: '#666' }}>No escalations</p> : escalation.by_category.map(function(c) {
                    return (
                      <div key={c._id} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #f0f0f0' }}>
                        <span style={{ fontSize: '13px' }}>{c._id}</span>
                        <span style={{ fontWeight: '700', color: '#f44336' }}>{c.count}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
          {activeTab === 'sla' && sla && (
            <div style={cardStyle}>
              <h3 style={{ margin: '0 0 4px', color: '#333' }}>SLA Breached Tickets</h3>
              <h1 style={{ margin: '0 0 16px', color: '#b71c1c', fontSize: '48px' }}>{sla.total_breached}</h1>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#f5f5f5' }}>
                    <th style={{ padding: '10px', textAlign: 'left', fontSize: '13px' }}>Ticket</th>
                    <th style={{ padding: '10px', textAlign: 'left', fontSize: '13px' }}>Customer</th>
                    <th style={{ padding: '10px', textAlign: 'left', fontSize: '13px' }}>Category</th>
                    <th style={{ padding: '10px', textAlign: 'left', fontSize: '13px' }}>Level</th>
                    <th style={{ padding: '10px', textAlign: 'left', fontSize: '13px' }}>Assigned To</th>
                    <th style={{ padding: '10px', textAlign: 'left', fontSize: '13px' }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {sla.tickets.length === 0 ? <tr><td colSpan="6" style={{ padding: '20px', textAlign: 'center', color: '#666' }}>No SLA breaches!</td></tr> : sla.tickets.map(function(t) {
                    return (
                      <tr key={t._id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                        <td style={{ padding: '10px', fontSize: '13px', fontWeight: '600', color: '#b71c1c' }}>{t.ticket_number}</td>
                        <td style={{ padding: '10px', fontSize: '13px' }}>{t.customer.name}</td>
                        <td style={{ padding: '10px', fontSize: '13px' }}>{t.complaint.category}</td>
                        <td style={{ padding: '10px', fontSize: '13px' }}>Level {t.escalation.current_level}</td>
                        <td style={{ padding: '10px', fontSize: '13px' }}>{t.assigned_to ? t.assigned_to.name : 'Unassigned'}</td>
                        <td style={{ padding: '10px' }}><button onClick={() => navigate('/tickets/' + t._id)} style={{ background: '#b71c1c', color: 'white', border: 'none', padding: '5px 12px', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}>View</button></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Reports;
